// A product binding is structural: plugin ids are shared by all languages and
// devices. Change it together with its descendants, in one undoable store update.
const isRecord = value => value && typeof value === 'object' && !Array.isArray(value);
const pluginId = cell => typeof cell.plugin === 'string' ? cell.plugin : cell.plugin?.id;

function cellData(cell, lang, device) {
    const variants = {...cell.dataI18n};
    if (device === 'MOBILE') {
        Object.entries(cell.dataMobI18n || {}).forEach(([key, value]) => {
            if (isRecord(value)) variants[key] = {...variants[key], ...value};
        });
    }
    return variants[lang] ?? Object.values(variants).find(Boolean) ?? {};
}

function mapChildren(node, transform) {
    let result = node;
    for (const key of ['rows', 'cells']) {
        if (!Array.isArray(node[key])) continue;
        const children = node[key].map(transform);
        if (children.some((child, index) => child !== node[key][index])) result = {...result, [key]: children};
    }
    return result;
}

function rebindCell(cell, action, newProduct) {
    const namespace = pluginId(cell).slice('form-product-info'.length);
    const oldProducts = new Set();
    for (const variants of [cell.dataI18n, cell.dataMobI18n]) {
        Object.values(variants || {}).forEach(data => {
            if (typeof data?.productId === 'string' && data.productId !== newProduct) oldProducts.add(data.productId);
        });
    }
    const prefixes = [...oldProducts].map(product => namespace.slice(1) + product + '/');
    const newPrefix = namespace.slice(1) + newProduct + '/';
    const remap = node => {
        if (!isRecord(node)) return node;
        let next = node;
        const id = pluginId(node);
        if (typeof id === 'string' && id.startsWith('form-')) {
            const slash = id.indexOf('/');
            const scope = id.slice(slash + 1);
            const previous = slash >= 0 && prefixes.find(prefix => scope.startsWith(prefix));
            if (previous) {
                const nextId = id.slice(0, slash + 1) + newPrefix + scope.slice(previous.length);
                next = {...node, plugin: typeof node.plugin === 'string' ? nextId : {...node.plugin, id: nextId}};
            }
        }
        return mapChildren(next, remap);
    };
    let next = mapChildren(cell, remap);
    const variantKey = action.device === 'MOBILE' ? 'dataMobI18n' : 'dataI18n';
    next = {...next, [variantKey]: {...next[variantKey],
        [action.lang]: {...cellData(cell, action.lang, action.device), ...next[variantKey]?.[action.lang], ...action.data}}};
    for (const key of ['dataI18n', 'dataMobI18n']) {
        if (!next[key]) continue;
        next[key] = Object.fromEntries(Object.entries(next[key]).map(([lang, data]) =>
            [lang, isRecord(data) ? {...data, productId: newProduct} : data]));
    }
    return next;
}

export function rebindProductInfo(value, action) {
    if (action?.type !== 'CELL_UPDATE_DATA' || !isRecord(action.data) ||
        typeof action.data.productId !== 'string' || !action.data.productId) return value;
    const visit = node => {
        if (!isRecord(node)) return node;
        const id = pluginId(node);
        if (node.id === action.id && typeof id === 'string' && id.startsWith('form-product-info/')) {
            if (cellData(node, action.lang, action.device).productId === action.data.productId) return node;
            return rebindCell(node, action, action.data.productId);
        }
        return mapChildren(node, visit);
    };
    return visit(value);
}

export const productInfoRebindingMiddleware = store => next => action => {
    const value = store.getState().reactPage?.values?.present;
    const rebound = rebindProductInfo(value, action);
    // UPDATE_VALUE belongs to the editor's normal undo/redo history. Preserve
    // action options (including notUndoable) and node ids/focus, without a remount.
    return next(rebound === value ? action : {...action, type: 'UPDATE_VALUE', value: rebound});
};

export const formEditorMiddleware = [productInfoRebindingMiddleware];
